import { Link } from "@tanstack/react-router";
import { AlertCircle, Check, Clock } from "lucide-react";
import { MetaText, StatusPill } from "@/components/plan-audit/AuditPrimitives";
import { ErrorState } from "@/components/shared/error-state";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { type RunDetail, useRun } from "@/hooks/use-plan-audit";
import { formatDuration, formatElapsed } from "@/lib/plan-audit-format";

/**
 * A run in progress, with the asset breakdown beside it.
 *
 * There is deliberately no percentage bar. A real audit reports which STAGE it is
 * in, not a fraction — the prototype animates a progress bar over a fixed seven
 * seconds, which is a prototype convenience, and inventing a percentage from a
 * stage index would be a number that looks precise and means nothing. The stage
 * list carries the same information honestly, and the hook re-reads every three
 * seconds while the run is live.
 */
export function RunProgressView({ reference }: { reference: string }) {
  const {
    data: run,
    isLoading,
    isError,
    error,
    refetch,
  } = useRun(reference, undefined, {
    refetchWhileRunning: true,
  });

  if (isLoading) {
    return (
      <div className="grid gap-4 lg:grid-cols-[1fr_320px]">
        <Skeleton className="h-80 rounded-xl" />
        <Skeleton className="h-80 rounded-xl" />
      </div>
    );
  }
  if (isError) return <ErrorState message={error.message} onRetry={() => void refetch()} />;
  if (!run) return <ErrorState message="Run not found" />;

  return (
    <div className="grid gap-4 lg:grid-cols-[1fr_320px] lg:items-start">
      <div className="pa-card">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="mb-1 flex flex-wrap items-center gap-2">
              <h2 className="font-display text-lg font-bold text-[var(--brand-primary)]">
                {run.solution.name}
              </h2>
              <StatusPill status={run.status} />
            </div>
            <MetaText>
              {run.planVersion.label} · run {run.reference} · Guide {run.guide.label}
              {run.status === "running"
                ? ` · started ${formatElapsed(run.startedAt)}`
                : run.status === "queued"
                  ? " · not started"
                  : run.durationSeconds !== null && ` · ${formatDuration(run.durationSeconds)}`}
            </MetaText>
            {run.executedByEmail.length > 0 && (
              <div>
                <MetaText>Executing on {run.executedByEmail}</MetaText>
              </div>
            )}
          </div>

          {run.status === "complete" && (
            <Button asChild>
              <Link to="/runs/$reference" params={{ reference: run.reference }}>
                View scorecard
              </Link>
            </Button>
          )}
        </div>

        {run.status === "failed" && (
          <div
            className="mt-4 flex gap-2 rounded-lg border p-3"
            style={{ background: "var(--danger-bg)", borderColor: "var(--danger)" }}
          >
            <AlertCircle
              aria-hidden="true"
              size={16}
              className="mt-0.5 shrink-0"
              style={{ color: "var(--danger)" }}
            />
            <div>
              <p className="text-[13px] font-semibold" style={{ color: "var(--danger-fg)" }}>
                This run did not finish
              </p>
              <p className="mt-0.5 text-xs" style={{ color: "var(--danger-fg)" }}>
                {run.failureReason || "No reason was recorded."}
              </p>
            </div>
          </div>
        )}

        {run.status === "queued" ? (
          <QueuedNotice run={run} onRefresh={() => void refetch()} />
        ) : (
          <StageList run={run} />
        )}
      </div>

      <AssetPanel run={run} />
    </div>
  );
}

/**
 * A queued run, said plainly.
 *
 * This is the state every run created from an upload sits in, because the audit
 * engine is not part of this build. Rendering the stage list here would show a
 * list of steps with none ticked, which reads as "in progress" — and the hook no
 * longer polls a queued run, so it would never change either. Saying so, with a
 * Refresh for when an engine does exist, is the honest version.
 */
function QueuedNotice({ run, onRefresh }: { run: RunDetail; onRefresh: () => void }) {
  return (
    <div
      className="mt-4 rounded-lg border p-4"
      style={{ background: "var(--warning-bg)", borderColor: "var(--warning)" }}
    >
      <div className="flex items-start gap-2">
        <Clock
          aria-hidden="true"
          size={16}
          className="mt-0.5 shrink-0"
          style={{ color: "var(--warning-fg)" }}
        />
        <div className="min-w-0">
          <p className="text-[13px] font-semibold" style={{ color: "var(--warning-fg)" }}>
            Waiting to be picked up
          </p>
          <p className="mt-1 text-xs" style={{ color: "var(--warning-fg)" }}>
            {run.planVersion.label} and its intake ({run.planVersion.intakeSummary}) are stored, and
            this audit is filed against Guide {run.guide.label}. Nothing is processing it yet —
            running an audit is not part of this build.
          </p>
          <Button variant="outline" size="sm" className="mt-3" onClick={onRefresh}>
            Refresh
          </Button>
        </div>
      </div>
    </div>
  );
}

function StageList({ run }: { run: RunDetail }) {
  if (run.stages.length === 0) {
    return (
      <p className="mt-4 text-[13px] text-[var(--fg-3)]">This run recorded no stage breakdown.</p>
    );
  }

  // With no per-stage timestamps stored, the honest reading is: a complete run
  // finished every stage, a failed run's progress is unknown, and a live run is
  // somewhere inside them. Nothing here claims to know WHICH stage is current —
  // that would be a guess dressed as status.
  const allDone = run.status === "complete";

  return (
    <ol className="mt-5 flex flex-col gap-4">
      {run.stages.map((stage, index) => (
        <li key={stage.title} className="flex gap-3">
          <span
            className="mt-0.5 grid size-6 shrink-0 place-items-center rounded-full text-[11px] font-bold"
            style={{
              background: allDone ? "var(--success)" : "var(--bg-3)",
              color: allDone ? "var(--fg-inverse)" : "var(--fg-3)",
            }}
            aria-hidden="true"
          >
            {allDone ? <Check size={13} strokeWidth={3} /> : index + 1}
          </span>
          <div className="min-w-0">
            <div className="text-[13px] font-semibold text-[var(--fg-1)]">{stage.title}</div>
            <p className="mt-0.5 text-xs leading-relaxed text-[var(--fg-2)]">{stage.detail}</p>
            <MetaText>{stage.reference}</MetaText>
          </div>
        </li>
      ))}
    </ol>
  );
}

/**
 * Every asset the run found in the plan. Missing and unrecognised entries are
 * tinted, because they are the only rows anyone needs to act on — a list of 26
 * green ticks is noise, and the two reds are the finding.
 */
function AssetPanel({ run }: { run: RunDetail }) {
  if (run.assets.length === 0) {
    return (
      <div className="pa-card">
        <div className="pa-card-head">
          <h3>Assets found</h3>
        </div>
        <p className="text-[13px] text-[var(--fg-3)]">No asset breakdown stored for this run.</p>
      </div>
    );
  }

  const missing = run.assets.filter((a) => a.state !== "ok").length;

  return (
    <div className="pa-card">
      <div className="pa-card-head">
        <h3>Assets found</h3>
        <MetaText>
          {run.assets.length} total{missing > 0 && ` · ${missing} need attention`}
        </MetaText>
      </div>
      <div className="pa-scroll flex flex-col gap-0.5 lg:max-h-[26rem]">
        {run.assets.map((asset) => {
          const tone =
            asset.state === "missing"
              ? { bg: "var(--danger-bg)", dot: "var(--danger)", weight: 600 }
              : asset.state === "unrecognized"
                ? { bg: "var(--warning-bg)", dot: "var(--warning)", weight: 600 }
                : { bg: "transparent", dot: "var(--success)", weight: 500 };
          return (
            <div
              key={asset.name}
              className="flex items-center gap-2 rounded-md px-2 py-1.5"
              style={{ background: tone.bg }}
            >
              <span
                className="size-1.5 shrink-0 rounded-full"
                style={{ background: tone.dot }}
                aria-hidden="true"
              />
              <span
                className="min-w-0 flex-1 truncate text-xs text-[var(--fg-1)]"
                style={{ fontWeight: tone.weight }}
              >
                {asset.name}
              </span>
              <span className="shrink-0 text-[11px] text-[var(--fg-3)] tabular-nums">
                {asset.state === "ok"
                  ? asset.count === null
                    ? "found"
                    : asset.count
                  : asset.state}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
